import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { maintenanceApi, MaintDeviceDetail as DeviceDetailType, MaintConnector, StationDropdownItem } from "../../../api/maintenance";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance, canUseRemoteCommands } from "../shared/MaintenanceGuard";
import ConnectorOverride from "./ConnectorOverride";
import DeviceLogsPanel from "./DeviceLogsPanel";
import RemoteCommandPanel from "./remoteCommands/RemoteCommandPanel";
import { ArrowLeft, Save, Zap, Upload, X, Link, Unlink } from "lucide-react";
import { useNotice } from "../shared/useNotice";

const DeviceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [device, setDevice] = useState<DeviceDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [message, setMessage] = useNotice("success", "");
  const [error, setError] = useNotice("error", "");
  const [uniqueName, setUniqueName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [overrideConnector, setOverrideConnector] = useState<MaintConnector | null>(null);

  // Station linking
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [stations, setStations] = useState<StationDropdownItem[]>([]);
  const [linkStationId, setLinkStationId] = useState<number | "">("");
  const [linkPosition, setLinkPosition] = useState(0);
  const [linking, setLinking] = useState(false);

  const fetchDevice = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await maintenanceApi.getDevice(parseInt(id));
      setDevice(res.data);
      setUniqueName(res.data.unique_name || "");
      setDisplayName(res.data.display_name || "");
    } catch (err) {
      setError("Failed to load device");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDevice();
  }, [fetchDevice]);

  const handleSave = async () => {
    if (!id || !device) return;
    const data: Record<string, string> = {};
    if (uniqueName !== (device.unique_name || "")) data.unique_name = uniqueName;
    if (displayName !== (device.display_name || "")) data.display_name = displayName;
    if (Object.keys(data).length === 0) {
      setMessage("No changes to save");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await maintenanceApi.updateDevice(parseInt(id), data);
      setMessage("Device updated");
      fetchDevice();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploadingLogo(true);
    setError("");
    try {
      await maintenanceApi.uploadDeviceLogo(parseInt(id), file);
      setMessage("Logo uploaded");
      fetchDevice();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleLogoDelete = async () => {
    if (!id || !device?.device_logo_url) return;
    if (!window.confirm("Remove device logo?")) return;
    try {
      await maintenanceApi.deleteDeviceLogo(parseInt(id));
      setMessage("Logo removed");
      fetchDevice();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to remove logo");
    }
  };

  const handleShowLink = async () => {
    try {
      const res = await maintenanceApi.getStationsDropdown();
      setStations(res.data);
      setShowLinkForm(true);
    } catch (err) {
      setError("Failed to load stations");
    }
  };

  const handleLinkStation = async () => {
    if (!id || !linkStationId) return;
    setLinking(true);
    setError("");
    try {
      await maintenanceApi.linkDeviceToStation(parseInt(id), {
        charging_station_id: linkStationId as number,
        station_position: linkPosition,
      });
      setMessage("Device linked to station");
      setShowLinkForm(false);
      fetchDevice();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to link");
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkStation = async () => {
    if (!id) return;
    if (!window.confirm("Unlink device from station?")) return;
    try {
      await maintenanceApi.unlinkDeviceFromStation(parseInt(id));
      setMessage("Device unlinked");
      fetchDevice();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to unlink");
    }
  };

  const handleOverrideComplete = () => {
    setOverrideConnector(null);
    fetchDevice();
  };

  if (loading) return <MaintenanceLayout title="Device Detail"><p>Loading...</p></MaintenanceLayout>;
  if (!device) return <MaintenanceLayout title="Device Detail"><p>Device not found</p></MaintenanceLayout>;

  return (
    <MaintenanceLayout title="Device Detail">
      <div className="maint-detail">
        <button className="maint-btn-link" onClick={() => navigate("/maintenance/devices")}>
          <ArrowLeft size={16} /> Back to Devices
        </button>

        <div className="maint-detail-card">
          <div className="maint-detail-header">
            <h3>{device.display_name || device.unique_name || `Device #${device.id}`}</h3>
            <span className={`maint-status maint-status-${device.device_status}`}>
              {device.device_status}
            </span>
          </div>

          <div className="maint-detail-info">
            <p><strong>Organization:</strong> {device.org_name || "Not linked"}</p>
            <p><strong>Station:</strong> {device.station_name || "Not linked"}
              {device.station_id && <> (pos: {device.station_position})</>}
            </p>
            <p><strong>OCPP Device ID:</strong> {device.ocpp_device_id || "-"}</p>
            <p><strong>OCPP Version:</strong> {device.ocpp_version || "-"}</p>
            <p><strong>Device GUID:</strong> {device.device_guid || "-"}</p>
            <p><strong>Last Active:</strong> {device.last_active ? new Date(device.last_active).toLocaleString() : "Never"}</p>
          </div>

          {/* Logo section */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: "8px" }}>Device Logo</label>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {device.device_logo_url ? (
                <>
                  <img src={device.device_logo_url} alt="Device logo" style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover", border: "1px solid #e0e0e0" }} />
                  {canEditMaintenance() && (
                    <button className="maint-btn-small" onClick={handleLogoDelete} title="Remove logo">
                      <X size={14} /> Remove
                    </button>
                  )}
                </>
              ) : (
                <span style={{ color: "#999" }}>No logo</span>
              )}
              {canEditMaintenance() && (
                <>
                  <input type="file" ref={fileInputRef} accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
                  <button className="maint-btn-small" onClick={() => fileInputRef.current?.click()} disabled={uploadingLogo}>
                    <Upload size={14} /> {uploadingLogo ? "Uploading..." : "Upload"}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Station linking */}
          {canEditMaintenance() && (
          <div style={{ marginBottom: "16px" }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: "8px" }}>Station Link</label>
            <div style={{ display: "flex", gap: "8px" }}>
              <button className="maint-btn-small" onClick={handleShowLink}>
                <Link size={14} /> {device.station_id ? "Re-link" : "Link to Station"}
              </button>
              {device.station_id && (
                <button className="maint-btn-small" onClick={handleUnlinkStation} style={{ color: "#c62828" }}>
                  <Unlink size={14} /> Unlink
                </button>
              )}
            </div>
            {showLinkForm && (
              <div style={{ marginTop: "8px", padding: "12px", background: "#f5f5f5", borderRadius: "8px" }}>
                <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                  <label style={{ flex: 1 }}>
                    Station
                    <select value={linkStationId} onChange={(e) => setLinkStationId(Number(e.target.value))}>
                      <option value="">Select station...</option>
                      {stations.map((s) => (
                        <option key={s.id} value={s.id}>{s.name} ({s.org_name})</option>
                      ))}
                    </select>
                  </label>
                  <label style={{ width: "100px" }}>
                    Position
                    <input type="number" value={linkPosition} onChange={(e) => setLinkPosition(parseInt(e.target.value) || 0)} />
                  </label>
                  <button className="maint-btn-primary" onClick={handleLinkStation} disabled={!linkStationId || linking} style={{ height: "38px" }}>
                    {linking ? "..." : "Link"}
                  </button>
                  <button className="maint-btn-secondary" onClick={() => setShowLinkForm(false)} style={{ height: "38px" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          <div className="maint-form-grid">
            <label>
              Unique Name
              <input type="text" value={uniqueName} onChange={(e) => setUniqueName(e.target.value)} />
            </label>
            <label>
              Display Name
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </label>
          </div>

          {message && <p className="maint-success">{message}</p>}
          {error && <p className="maint-form-error">{error}</p>}

          {canEditMaintenance() && (
            <div className="maint-detail-actions">
              <button onClick={handleSave} disabled={saving} className="maint-btn-primary">
                <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )}
        </div>

        {/* Connectors */}
        <div className="maint-detail-card" style={{ marginTop: "16px" }}>
          <h3>Connectors</h3>
          {device.connectors.length === 0 ? (
            <p className="maint-empty">No connectors</p>
          ) : (
            <table className="maint-table">
              <thead>
                <tr>
                  <th>Connector</th>
                  <th>Socket Type</th>
                  <th>Status</th>
                  <th>Capacity (kWh)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {device.connectors.map((c) => (
                  <tr key={c.id}>
                    <td>{c.connector_name}</td>
                    <td>{c.socket_type || "-"}</td>
                    <td>
                      <span className={`maint-status maint-status-${(c.status || "unknown").toLowerCase()}`}>
                        {c.status || "Unknown"}
                      </span>
                    </td>
                    <td>{c.capacity_kwh ?? "-"}</td>
                    <td>
                      {canEditMaintenance() && (
                        <button
                          className="maint-btn-small"
                          onClick={() => setOverrideConnector(c)}
                          title="Override status"
                        >
                          <Zap size={14} /> Override
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Live Device Logs (SSE) */}
        <div className="maint-detail-card" style={{ marginTop: "16px" }}>
          <DeviceLogsPanel deviceId={device.id} uniqueName={device.unique_name} />
        </div>

        {/* Remote OCPP Commands */}
        {canUseRemoteCommands() && (
          <div className="maint-detail-card" style={{ marginTop: "16px" }}>
            <RemoteCommandPanel deviceId={device.id} uniqueName={device.unique_name} />
          </div>
        )}

        {canEditMaintenance() && overrideConnector && device && (
          <ConnectorOverride
            deviceId={device.id}
            connector={overrideConnector}
            onClose={() => setOverrideConnector(null)}
            onComplete={handleOverrideComplete}
          />
        )}
      </div>
    </MaintenanceLayout>
  );
};

export default DeviceDetail;
